import React from 'react';
import type { Project, ProjectLink, ProjectLinkType } from '../firebase/projectService';

type IconProps = { className?: string };

// Brand-accurate inline SVG icons (currentColor inherits the surrounding text color).
const GlobeIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const GooglePlayIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M3.609 1.814 13.792 12 3.609 22.186a1.5 1.5 0 0 1-.61-1.21V3.024a1.5 1.5 0 0 1 .61-1.21zm10.89 10.89 2.302 2.302-9.71 5.6 7.408-7.902zm3.792-3.792 2.55 1.47c.98.566.98 1.98 0 2.546l-2.55 1.47-2.55-2.743 2.55-2.743zM6.79 2.498l9.71 5.6-2.302 2.302L6.79 2.498z" />
  </svg>
);

const AppleIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const FacebookIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

export interface PlatformMeta {
  type: ProjectLinkType;
  /** Short label used for chips/checkboxes/badges (e.g. "Website") */
  label: string;
  /** Full call-to-action used for buttons (e.g. "Get it on Google Play") */
  ctaLabel: string;
  /** Brand color (hex) */
  color: string;
  /** Icon component, accepts a className */
  Icon: React.FC<IconProps>;
  /** Placeholder URL shown in the admin input */
  placeholder: string;
  /** Hostname fragments that indicate a correct URL for this platform (soft validation) */
  hostHints: string[];
}

/**
 * Single source of truth for everything platform-specific about a live link.
 * Add a new platform here (and to ProjectLinkType) and the whole UI picks it up.
 */
export const PLATFORM_META: Record<ProjectLinkType, PlatformMeta> = {
  website: {
    type: 'website',
    label: 'Website',
    ctaLabel: 'Visit Website',
    color: '#2563EB', // blue-600 (matches primary)
    Icon: GlobeIcon,
    placeholder: 'https://yourproject.com',
    hostHints: [], // any host is acceptable for a website
  },
  playstore: {
    type: 'playstore',
    label: 'Play Store',
    ctaLabel: 'Get it on Google Play',
    color: '#01875F', // Google Play green
    Icon: GooglePlayIcon,
    placeholder: 'https://play.google.com/store/apps/details?id=...',
    hostHints: ['play.google.com'],
  },
  appstore: {
    type: 'appstore',
    label: 'App Store',
    ctaLabel: 'Download on the App Store',
    color: '#0D96F6', // Apple App Store blue
    Icon: AppleIcon,
    placeholder: 'https://apps.apple.com/app/id...',
    hostHints: ['apps.apple.com', 'itunes.apple.com'],
  },
  facebook: {
    type: 'facebook',
    label: 'Facebook Page',
    ctaLabel: 'Follow on Facebook',
    color: '#1877F2', // Facebook blue
    Icon: FacebookIcon,
    placeholder: 'https://facebook.com/yourpage',
    hostHints: ['facebook.com', 'fb.com', 'fb.me'],
  },
};

/** Display/checkbox order of the platforms */
export const PLATFORM_ORDER: ProjectLinkType[] = ['website', 'playstore', 'appstore', 'facebook'];

/** Basic URL validity check (same approach as the gallery URL validator) */
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Soft host check: returns true if the URL looks like it belongs to the platform
 * (or if the platform has no host expectation, e.g. a website). Used for non-blocking
 * warnings in the admin form.
 */
export const matchesPlatformHost = (type: ProjectLinkType, url: string): boolean => {
  const hints = PLATFORM_META[type].hostHints;
  if (hints.length === 0) return true;
  try {
    const host = new URL(url).hostname.toLowerCase();
    return hints.some(hint => host.includes(hint));
  } catch {
    return false;
  }
};

/**
 * Merge the new `links` array with the legacy `link` field into a single, clean list.
 * - De-duplicates by platform type (new `links` win over the legacy fallback).
 * - Drops entries with empty/whitespace URLs.
 * - Orders results by PLATFORM_ORDER for consistent rendering everywhere.
 *
 * Every UI consumer should use this instead of reading `project.link` directly.
 */
export const normalizeProjectLinks = (
  project: Pick<Project, 'links' | 'link'>
): ProjectLink[] => {
  const byType = new Map<ProjectLinkType, string>();

  // Legacy single link counts as a website link (lowest priority).
  if (project.link && project.link.trim()) {
    byType.set('website', project.link.trim());
  }

  // New structured links override the legacy fallback.
  if (Array.isArray(project.links)) {
    for (const entry of project.links) {
      if (entry && entry.type && entry.url && entry.url.trim()) {
        byType.set(entry.type, entry.url.trim());
      }
    }
  }

  return PLATFORM_ORDER.filter(type => byType.has(type)).map(type => ({
    type,
    url: byType.get(type) as string,
  }));
};
